import { cn } from "@/libs/utils";
import {
  getProductDetailHref,
  isProductDetailActive,
} from "@/libs/product/detail-href";
import {
  type BadgeType,
  type Menu,
  type PanelKey,
} from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import { useBundleSetBadges } from "@/v2-components/global-navigation-bar/use-bundle-set-badges";
import type { ReactElement } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import type { PopularGamesPlatform } from "@/pages/api/games/popular";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { SwitchCase } from "@/v2-components/switch-case";
import { ProductUtil } from "@/utils/ProductUtil";
import { Badge } from "@/v2-components/ui/badge";
import { useProducts } from "@/services/product/useProductService";
import { useTrans } from "@/utils/useTrans";
import TactSuitCard from "@/v2-components/TactSuitCard";
import { SUPPORT_HUB_URL } from "@/constants/Urls.ts";
import { MenuItemBadge } from "@/v2-components/global-navigation-bar/menu-item-badge";
import { PopularTitlesRail } from "@/v2-components/global-navigation-bar/popular-titles-rail";
import { useLocale } from "@/store/locale-store";
import { Product } from "@/api/product-api";
import { getSubNavTabs } from "@/v2-components/pages/games/GamesSubNav";
import {
  STORE_COPY,
  STORE_GROUP_LABELS,
  splitStoreGroups,
  storeRowBadge,
} from "@/v2-components/global-navigation-bar/_const/store-panel";
import { usePricedFilter } from "@/hooks/usePricedFilter";

/**
 * 분기 키는 **메뉴 이름이 아니라 `menu.panel`** 이다. 이름으로 분기하면 라벨을 바꿀 때
 * 이 파일과 모바일(`menu-view.tsx`)이 갈라져 한쪽이 조용히 아무것도 안 그린다 — v1 이
 * 그 상태였다.
 *
 * ⚠️ `SwitchCase` 의 `caseBy` 는 `Partial<Record<…>>` 라서 **케이스를 빼도 tsc 가 잡지
 * 않는다**(런타임에 `null` 을 렌더할 뿐이다). 그래서 `Record<PanelKey, …>` 로 한 번 받아
 * 전수를 강제한다 — `PanelKey` 에 값을 추가하면 여기서 컴파일이 깨진다.
 */
export function NavigationMenuContent({
  menu,
  onClose,
}: {
  menu: Menu & { panel: PanelKey };
  onClose: (e?: any) => void;
}) {
  const router = useRouter();

  const byPanel: Record<PanelKey, ReactElement> = {
    store: <TactSuitContent onClick={onClose} />,
    pc: <GamesContent onClick={onClose} platform="pc" />,
    vr: <GamesContent onClick={onClose} platform="vr" />,
    support: <SupportContent currentPath={router.asPath} onClick={onClose} />,
  };

  return <SwitchCase<PanelKey> value={menu.panel} caseBy={byPanel} />;
}

function GamesContent({
  onClick,
  platform,
}: {
  onClick: (e: any) => void;
  /** 인기 타이틀 레일의 플랫폼. `pc` / `vr` 패널이 각자 다른 목록을 받는다. */
  platform: PopularGamesPlatform;
}) {
  const { languageCode } = useLocale();
  const { t } = useTrans(["gnb"]);

  /**
   * ★**시안의 최종 배치로 교체했다** (#5415 · `29212:35874` PC · `29212:35926` VR).
   *
   * 종전엔 카테고리 링크 그룹(모든 게임 · 메타 퀘스트 · 스팀 VR …)이었고, 그 자리 주석이
   * *"시안(§4)의 최종 배치는 sub-nav 3개(342w) + 레일(708w)이고, 그건 패널 콘텐츠 PR 에서
   * 들어온다"* 라고 예고해 뒀다. 레일은 이미 붙었고 이번에 좌측을 바꾼다.
   *
   * ⚠️ **탭 정의를 여기 복제하지 않는다.** `getSubNavTabs` 가 단일 출처다 — 페이지
   * 서브내비와 GNB 패널이 갈라지면 「같은 세 항목」이라는 전제가 조용히 깨진다.
   *
   * ★**라벨 덮어쓰기를 걷었다** (2026-09-07 요청). 종전엔 가운데만 갈랐다:
   *
   *   서브내비   Overview · PC Games / VR Games · Software
   *   GNB 패널   Overview · **All supported games** · Software
   *
   * *"화면 안에서는 「PC 게임」, 메뉴에서는 「전체」를 강조"* 가 근거였는데, 이제 둘을
   * **같은 문구**(`Supported PC Games` / `Supported VR Games`)로 맞춘다. 덮어쓰기가
   * 없어져 `tab.label` 을 그대로 쓴다 — 갈라질 자리 자체가 사라졌다.
   */
  const tabs = getSubNavTabs(platform === "vr" ? "/games/vr" : "/games/pc");

  return (
    <div className="flex w-full justify-center border-b border-coolgray-20 bg-white pb-9 pt-8">
      {/* ⚠️ 패널 3종 모두 `w-screen` 금지 (#5831) — `100vw` 는 세로 스크롤바 폭을 포함해
          Windows 에서 열 때마다 가로 스크롤이 생긴다. 래퍼가 이미 `absolute left-0 w-full`. */}
      {/* 시안 `cols` — sub-nav 342 + gap 146 + 레일 708. */}
      <div className="flex items-start gap-x-[146px]">
        {/* 시안 `sub-nav` (`29230:21400`) — 항목 `px-12 py-9 rounded-8` · 14/18 · 간격 2. */}
        <nav className="flex w-[342px] flex-col gap-y-0.5">
          {tabs.map((tab, i) => (
            <Link
              key={tab.href}
              locale={languageCode}
              href={tab.href}
              onClick={onClick}
              className={cn(
                "rounded-lg px-3 py-[9px] text-base leading-[18px] tracking-[0.25px] transition-colors hover:bg-coolgray-10",
                // 시안은 첫 항목만 진한 색이다(`#21272a` vs `#4d5358`) — 활성 표시가
                // 아니라 위계 표현이라 경로와 무관하게 첫 항목에 준다.
                i === 0 ? "text-coolgray-90" : "text-coolgray-70",
              )}
            >
              {t(tab.label)}
            </Link>
          ))}
        </nav>

        <PopularTitlesRail platform={platform} onClick={onClick} />
      </div>
    </div>
  );
}

/**
 * Store 2deps 패널 (시안 `29212:35770` `2deps-Store`).
 *
 * ★**3열 구성이다** — 종전엔 「Explore 링크 열 + 신체부위별 제품 그리드」였다:
 *
 *   col/햅틱 수트 — 몸통으로 느끼는 메인 기어   464w   Torso 제품
 *   col/확장 기어 — 수트에 더하는 몰입          464w   나머지 신체부위
 *   col/전체 라인업 — 세트와 액세서리까지        342w   번들·액세서리·전체 보기
 *
 * ⚠️ **제품 목록은 데이터가 정한다** — 시안은 5종(Pro · Air · TactSleeve · TactGlove ·
 * TactVisor)만 그렸지만 실제로는 `TactSuit Air: Camo` · `TactGlove DK3` ·
 * `Tactosy for Feet` 도 **판매 중**이다. 시안을 그대로 하드코딩하면 파는 물건이
 * 메뉴에서 사라진다 — `useProducts` 결과를 그대로 쓰고 그룹만 시안대로 나눈다.
 *
 * ⚠️ **배지는 `Sale` 하나다** (#5603). 시안 행에는 배지가 없지만 그건 라이브 커머스
 * 신호이고(`product.tags`), 시안을 그린 날 그 5종에 `sale` 이 없었을 뿐이다.
 * 종전 이 주석은 *"배지는 남긴다"* 였는데 **실제로는 그리지 않고 있었다** — 주석이
 * 코드보다 앞서 있던 자리다. 술어는 `storeRowBadge`(`_const/store-panel.ts`) 한 곳이고
 * 모바일이 같은 것을 읽는다. `New` 는 붙이지 않는다 — prod 실측(2026-09-08)에서
 * 액세서리 11건 + 디바이스 2건에 걸려 있어 붙이면 패널이 배지로 덮인다.
 */
function TactSuitContent({ onClick }: { onClick: (e: any) => void }) {
  const { t } = useTrans(["gnb"]);
  const { data: products } = useProducts();
  const priced = usePricedFilter();
  const { torso, extras } = splitStoreGroups(priced(products));
  const bundleBadges = useBundleSetBadges();

  return (
    <div className="flex w-full justify-center border-b border-coolgray-20 bg-white pb-9 pt-8">
      {/* ★`px-4` 가 아니라 `mx-4` 다 — GNB 바(`index.tsx:65`)가 `mx-4 max-w-screen-max`
          라서 패딩을 쓰면 열 우측이 바보다 16px 안쪽으로 들어간다(실측 1664 vs 1680).
          시안도 cols 우측끝이 컨테이너 끝과 같다. */}
      <div className="mx-4 flex w-full max-w-screen-max justify-end gap-x-6">
        <StoreColumn
          label={STORE_GROUP_LABELS.torso}
          products={torso}
          onClick={onClick}
        />
        <StoreColumn
          label={STORE_GROUP_LABELS.extras}
          products={extras}
          onClick={onClick}
        />

        {/* 시안 `col/전체 라인업`(`29229:21432`) — 링크 3개(342w). 라벨 형태는 앞의 두
            열과 같지만 **간격만 2px 다**(앞 두 열은 4px) — 행이 아니라 링크라서. */}
        <div className="flex w-[342px] shrink-0 flex-col gap-0.5">
          <div className="px-3 pb-2 text-xs font-500 leading-4 tracking-[0.5px] text-coolgray-50">
            {t(STORE_GROUP_LABELS.lineup)}
          </div>
          <StoreLink
            href="/tactsuit/bundles"
            onClick={onClick}
            badges={bundleBadges}
          >
            {t("Bundle Set")}
          </StoreLink>
          <StoreLink href="/tactsuit/accessories" onClick={onClick}>
            {t("Accessories")}
          </StoreLink>
          {/* ★마지막 줄만 스타일이 다르다 — 시안 `29229:21440` 은 12/16 SemiBold
              `blue-60` 이고 위 둘은 14/18 Regular `coolgray-70` 이다. */}
          <StoreLink href="/tactsuit" onClick={onClick} cta>
            {t("See all products")} →
          </StoreLink>
        </div>
      </div>
    </div>
  );
}

/**
 * 시안 `col/*`(`29229:21383` 햅틱 수트 · `29229:21400` 확장 기어) — 라벨 + 제품 행.
 *
 * ⚠️ **`LinkCard` 를 재사용하지 않는다.** 그건 종전 그리드용이라 구조가 다르다 —
 * 썸네일 80 정사각 · 이름 16px · 설명 없음. 시안은 썸네일 **72 r12** · 이름 **14/18** ·
 * **한 줄 설명**이 있고 그 설명이 **두 색으로 나뉜다**(햅틱 수트 열). 겉이 비슷하다고
 * 재사용하면 그 차이가 통째로 빠진다 — 실제로 첫 판에서 그렇게 빠졌다.
 */
const StoreColumn = ({
  label,
  products,
  onClick,
}: {
  label: string;
  products: Product[];
  onClick: (e: any) => void;
}) => {
  const { t } = useTrans(["gnb"]);
  if (products.length === 0) return null;
  return (
    // 시안 `col/*` — 라벨↔행 및 행 사이 간격 4.
    <div className="flex w-[464px] shrink-0 flex-col gap-1">
      {/* 시안 `group-label` — `px-12 pb-8` · 11/16 Medium `coolgray-50` tracking .5. */}
      <div className="px-3 pb-2 text-xs font-500 leading-4 tracking-[0.5px] text-coolgray-50">
        {t(label)}
      </div>
      {products.map((product) => (
        <StoreRow key={product.id} product={product} onClick={onClick} />
      ))}
    </div>
  );
};

/**
 * 시안 `row-*` — `px-12 py-10 rounded-10 gap-14 items-center`.
 *
 * 문구·순서·그룹 정의는 `_const/store-panel.ts` 한 곳에 있다 — 모바일 시트가 같은 것을
 * 읽는다. 여기 두면 두 화면이 갈라진다(이 PR 이 Support 메뉴에서 겪은 그 자리다).
 */
const StoreRow = ({
  product,
  onClick,
}: {
  product: Product;
  onClick: (e: any) => void;
}) => {
  const { t } = useTrans(["gnb"]);
  const router = useRouter();
  // 제품은 동적 라우트라 `pathname` 으로 못 가른다 — 공유 primitive 를 쓴다(#5386).
  const current = isProductDetailActive(router.asPath, product.slug);
  const copy = STORE_COPY[product.slug];
  /**
   * ★**시안 우선.** 시안이 그린 5종은 리포가 소유한 이미지를 쓰고, 시안에 없는 제품
   * (TactSuit Air: Camo 등)만 API 값으로 떨어진다 — 실측에서 API 썸네일이 시안과
   * 2장 달랐다(Air 색상 · Glove 한 짝/두 짝). 근거는 `_const/store-panel.ts` 주석.
   */
  const thumb = copy?.thumb ?? product.thumbnail;

  return (
    <Link
      href={getProductDetailHref(product.slug)}
      onClick={onClick}
      className="flex w-full items-center gap-3.5 rounded-[10px] px-3 py-2.5 transition-colors hover:bg-coolgray-10"
    >
      {/* 시안 `thumb` — 72 · r12 · `object-contain`. */}
      <div className="relative size-[72px] shrink-0 overflow-hidden rounded-xl">
        {thumb && (
          <Image
            src={thumb}
            alt=""
            fill
            className="object-contain"
            sizes="72px"
          />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-base font-600 leading-[18px] tracking-[0.25px] text-coolgray-90",
                current && "underline",
              )}
            >
              {product.name}
            </span>
            {/* ★세일 중인 제품에 `Sale` (#5603). 술어는 `storeRowBadge` 한 곳이고
                모바일(`menu-view.tsx`)이 같은 것을 읽는다 — 각자 조건을 쓰면 갈라진다. */}
            <MenuItemBadge badge={storeRowBadge(product)} size="sm" />
          </div>
          {copy && (
            /* 시안 `desc`(`29229:21392` 등) — 12/16 tracking .4 · `coolgray-50`.
               ⚠️ 종전엔 앞부분을 초록으로 강조하고(`highlight`) 아래에 배지(`tags`)를
               달았는데, 시안이 그 둘을 **`hidden="true"` 로 껐다**(`29229:21391`·
               `21398`·`21409`·`21429`). 한 줄 회색 설명만 남는다. */
            <span className="text-sm leading-4 tracking-[0.4px] text-coolgray-50">
              {t(copy.desc)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

/**
 * 시안 `link-*` — `px-12 py-9 rounded-8` · 14/18. sub-nav 와 같은 형태다.
 *
 * ⚠️ **정적 경로 전용이다.** 볼드 판정에 `router.pathname === href` 를 쓰는데, 그건
 * **동적 라우트에서 절대 참이 되지 않는다**(PDP 는 `pathname` 이 `/shop/buy-tactsuit/[slug]`
 * 라는 패턴 문자열이라 — #5386 에서 데스크톱 제품 볼드가 영구히 false 였던 원인).
 * 그래서 `href` 를 **리터럴 유니온**으로 좁혀 동적 경로가 아예 못 들어오게 한다.
 * 제품처럼 동적인 것은 `LinkCard`(`isProductDetailActive`)를 쓴다.
 */
type StoreLinkHref =
  "/tactsuit" | "/tactsuit/bundles" | "/tactsuit/accessories";

const StoreLink = ({
  href,
  onClick,
  cta,
  badges,
  children,
}: {
  href: StoreLinkHref;
  onClick: (e: any) => void;
  /** 시안 `link-전체 제품 보기 →`(`29229:21440`) — 12/16 SemiBold `blue-60`. */
  cta?: boolean;
  /** `Bundle Set` 행만 받는다 — 시안이 그 한 행에만 `BadgeArea` 를 뒀다. */
  badges?: BadgeType[];
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const { languageCode } = useLocale();
  return (
    <Link
      locale={languageCode}
      href={href}
      onClick={onClick}
      className={cn(
        // 시안 `29229:21435` — 라벨과 배지가 `gap-8`(=gap-2) 로 나란하다.
        "flex items-center gap-2 rounded-lg px-3 py-[9px] transition-colors hover:bg-coolgray-10",
        cta
          ? "text-sm font-600 leading-4 tracking-[0.4px] text-blue-60"
          : "text-base leading-[18px] tracking-[0.25px] text-coolgray-70",
        !cta && router.pathname === href && "font-600 text-coolgray-90",
      )}
    >
      {/* 자식을 한 요소로 묶는다. `See all products →` 는 문자열 + " →" 로 자식이 둘인데,
          flex 로 바꾸면 그 둘이 각각 아이템이 되어 `gap-2` 가 화살표 앞에 8px 을 넣는다. */}
      <span>{children}</span>
      {!!badges?.length && (
        <span className="flex items-center gap-1">
          {badges.map((badge) => (
            <MenuItemBadge
              key={badge}
              badge={badge}
              size="sm"
              // 시안 배지 타이포는 `+0.1px` 다. `size="sm"` 은 자간을 안 정해서
              // 링크의 `tracking-[0.25px]` 를 상속했고, 실측에서 모바일 `Sale` 폭이
              // **38 vs 시안 37** 로 1px 어긋났다.
              className="tracking-[0.1px]"
            />
          ))}
        </span>
      )}
    </Link>
  );
};

const LinkCard = ({
  onClick,
  product,
  href,
}: {
  onClick: (e: any) => void;
  href: string;
  product: Product;
}) => {
  const router = useRouter();
  // ★`router.pathname` 으로 비교하지 않는다 (#5386). PDP 는 동적 라우트라 `pathname` 이
  // `/shop/buy-tactsuit/[slug]` 라는 **패턴 문자열**이고, 이 카드가 받는 href 는
  // `getProductDetailHref(product.slug)`(호출처 2곳) 이라 **절대 같아지지 않는다** —
  // 데스크톱 메뉴의 제품 볼드가 영구히 false 였다.
  //
  // camo 는 URL 이 베이스 제품의 `?color=camo` 로 정규화되므로 경로만으로도 못 가른다.
  // 판정은 `isProductDetailActive` 한 곳을 쓴다(모바일 메뉴와 같은 primitive).
  //
  // ⚠️ 같은 파일의 `:68`·`:235`·`:245`·`:260` 은 **정적 경로**(`/games/...`·`/tactsuit`·
  // `/tactsuit/bundles`·`/tactsuit/accessories`)라 `pathname` 비교가 맞다 — 건드리지 않는다.
  const current = isProductDetailActive(router.asPath, product.slug);
  return (
    <Link href={href} onClick={onClick} className="block">
      <TactSuitCard product={product}>
        <div className="group flex items-center gap-x-3">
          <div className="relative aspect-square min-w-[80px]">
            <TactSuitCard.Thumbnail fill className="hover-scale" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <TactSuitCard.Tags />
            <TactSuitCard.Name
              className={cn("text-lg", current && "font-600")}
            />
            <TactSuitCard.MetaBadge className="aspect-[90/20] max-h-[20px] max-w-[90px]" />
          </div>
        </div>
      </TactSuitCard>
    </Link>
  );
};

const LinkMenu = ({
  onClick,
  currentPath,
  title,
  items,
}: {
  items: { title: string; href: string }[];
  title: string;
  onClick: (e: any) => void;
  currentPath: string;
}) => {
  const { t } = useTrans(["gnb"]);
  return (
    <div className="flex flex-col gap-y-3">
      <Title>{t(title)}</Title>
      <div className="flex flex-col gap-y-4">
        {items.map((item, index) => {
          const current = currentPath.includes(item.href);
          return (
            <Link
              key={index}
              href={item.href}
              onClick={onClick}
              className={cn("relative hover:font-600", current && "font-600")}
            >
              <Label className="invisible relative whitespace-nowrap font-600">
                {t(item.title)}
              </Label>
              <Label className="absolute left-0 top-0 whitespace-nowrap">
                {t(item.title)}
              </Label>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <span className={cn("text-sm font-500 text-coolgray-50", className)}>
      {children}
    </span>
  );
};

const Label = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h4 className={cn("text-lg text-coolgray-90", className)}>{children}</h4>
  );
};

/**
 * 2뎁스 패널을 **1차 메뉴 라벨과 좌측 정렬**하기 위해, 그 라벨의 x 를 기준 요소 대비
 * offset 으로 잰다 (#5577 · 디자이너 *"2deps 메뉴명 좌측 정렬"*).
 *
 * ## 왜 CSS 로 안 되나
 *
 * 오른쪽 묶음 트리거는 **우단만 고정**이고 라벨 x 는 라벨 폭만큼 움직인다 —
 * 2026-09-07 실측(1440):
 *
 * ```
 *              라벨 텍스트 x    트리거 우단
 *   en Support      1154            1261
 *   ko 지원         1185            1261     ← 31px 차이
 * ```
 *
 * 그래서 `mr-[62px]` 류의 고정 offset 은 **로케일마다 어긋난다.** 7개 로케일 라벨 폭을
 * 코드에 적는 것도 같은 문제를 옮기는 것뿐이다.
 *
 * ## 읽는 대상은 셀렉터가 아니라 `data-gnb-label` 계약이다
 *
 * `navigation-menu.tsx` 의 `BoldSafeLabel` 이 그 속성을 단다. 그 span 의 left 가 곧
 * 보이는 라벨 텍스트의 left 다(안쪽 절대배치 span 이 `left-0`).
 *
 * 패널과 트리거는 **같은 positioned 조상**(바 루트) 안에 있어서 두 rect 의 차가
 * 그대로 쓸 수 있는 offset 이 된다.
 *
 * ⚠️ 못 찾으면 `null` 을 돌려주고 호출처는 **종전 정렬로 폴백**한다 — 정렬이 안 맞을
 * 뿐 메뉴가 깨지지는 않는다.
 */
function useTriggerLabelOffset(labelKey: string) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState<number | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const label = document.querySelector<HTMLElement>(
      `[data-gnb-label="${labelKey}"]`,
    );
    if (!anchor || !label) return;

    const measure = () =>
      setOffset(
        label.getBoundingClientRect().left -
          anchor.getBoundingClientRect().left,
      );

    // 패널은 포인터가 네비에 들어온 순간 마운트된다 — `useLayoutEffect` 라 첫 페인트
    // 전에 값이 잡히므로 「우측 정렬 → 좌측 정렬」로 한 프레임 튀지 않는다.
    measure();

    // 라벨 폭은 로케일 전환·폰트 로드로도 바뀐다. 폭 변화와 리사이즈를 둘 다 본다.
    const ro = new ResizeObserver(measure);
    ro.observe(label);
    ro.observe(anchor);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [labelKey]);

  return { anchorRef, offset };
}

export function SupportContent({
  onClick,
  currentPath,
}: {
  onClick: (e: any) => void;
  currentPath: string;
}) {
  const { t } = useTrans(["gnb"]);
  const { anchorRef, offset } = useTriggerLabelOffset("support");

  /**
   * 시안 `menu-Support`(`29247:21398`) — **220w 한 열에 4개**. 그룹 제목은 없다.
   *
   * ⚠️ 이 목록은 `SUPPORT_SUBMENUS`(모바일이 쓴다)와 **별개의 하드코딩**이라 한쪽만
   * 고치면 데스크탑/모바일이 갈라진다 — 둘 다 함께 고칠 것. 모바일 시안
   * (`29304:241277`)도 같은 4개다.
   */
  const items = [
    {
      title: "Support Hub",
      href: SUPPORT_HUB_URL.home,
      target: "_blank" as const,
    },
    // ★기본 `type` 을 박는다 — 없으면 `downloads.tsx` 가 `?type=manual` 로 리다이렉트한다
    //   (prod 실측 2026-09-17: 홉 1). 형제 목록(`navigation-menus.ts` 의
    //   `SUPPORT_SUBMENUS`)·푸터와 **문자까지 같은 값**이다 (#5795 R1 F3).
    //
    //   ⚠️ **끝슬래시를 쓰지 않는다** — 이 값은 `<Link>` 를 지나는데 그 정규화가
    //   `trailingSlash` 설정에 의존한다. 실측(2026-09-17 · 같은 `<Link>` 두 개를 렌더):
    //   jest(jsdom · 설정 없음)는 `…/downloads/?type=manual` 에서 슬래시를 **떼고**,
    //   prod(`trailingSlash: true`)는 무슬래시에 **붙인다.** 즉 슬래시를 적으면 값이
    //   환경마다 달라지고 `gnb-support-menu.test.tsx` 의 단언이 **우연히** 맞게 된다
    //   (그 통과의 주체는 `norm` 이 아니라 Next 였다). 무슬래시는 양쪽에서 같다.
    { title: "Downloads", href: "/support/downloads?type=manual" },
    { title: "About bHaptics", href: "/about" },
    { title: "Where to Experience", href: "/business/where-to-try" },
  ];

  return (
    <div className="flex w-full justify-center border-b border-coolgray-20 bg-white pb-6 pt-5">
      {/* Store 패널과 같은 컨테이너 표기 — `px-4` 를 쓰면 GNB 바(`mx-4`)보다 16px
          안쪽으로 들어간다. */}
      {/**
       * ★`justify-end` 를 버리고 **라벨 x 에 맞춘 `marginLeft`** 로 놓는다 (#5577).
       *
       * 종전엔 오른쪽 정렬이라 항목 텍스트가 라벨보다 **62px 오른쪽**이었다
       * (2026-09-07 실측 1440 en: 라벨 1154 · 항목 텍스트 1216).
       *
       * 항목은 `px-3`(12) 을 갖고 있으므로 **그만큼 당겨야** 텍스트끼리 맞는다 —
       * `marginLeft = 라벨offset − 12`. 박스가 아니라 **글자**를 정렬한다.
       *
       * 못 재면(`offset === null`) `auto` 로 종전 우측 정렬을 유지한다.
       */}
      <div ref={anchorRef} className="mx-4 flex w-full max-w-screen-max">
        <nav
          className="flex w-[220px] flex-col gap-0.5"
          style={{ marginLeft: offset === null ? "auto" : offset - 12 }}
        >
          {items.map((item, i) => {
            const current = currentPath.includes(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                target={item.target}
                onClick={onClick}
                className={cn(
                  "rounded-lg px-3 py-[9px] text-base leading-[18px] tracking-[0.25px] text-coolgray-70 transition-colors hover:bg-coolgray-10",
                  // 시안은 첫 항목만 SemiBold 다 — 경로와 무관한 위계 표현이라
                  // 게임 sub-nav 의 「첫 항목만 진한 색」과 같은 축으로 본다.
                  i === 0 && "font-600",
                  current && "text-coolgray-90",
                )}
              >
                {t(item.title)}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
